import { supabase } from './supabase'

/**
 * Validates if a tournament is ready for match generation.
 * @param {string} tournamentId 
 * @returns {Promise<Object>} { isReady, steps, blocker }
 */
export const validateTournament = async (tournamentId) => {
    if (!tournamentId) return { isReady: false, steps: [], blocker: null }

    try {
        // 1. Fetch all counts AND tournament details
        const [
            { data: tournament },
            { count: divisionsCount },
            { count: poulesCount },
            { count: fieldsCount },
            { count: teamsCount },
            { data: teamsWithoutPoule }
        ] = await Promise.all([
            supabase.from('tournaments').select('type, tenant_id').eq('id', tournamentId).single(),
            supabase.from('divisions').select('*', { count: 'exact', head: true }).eq('tournament_id', tournamentId),
            supabase.from('poules').select('*', { count: 'exact', head: true }).eq('tournament_id', tournamentId),
            supabase.from('fields').select('*', { count: 'exact', head: true }).eq('tournament_id', tournamentId),
            supabase.from('teams').select('*', { count: 'exact', head: true }).eq('tournament_id', tournamentId),
            supabase.from('teams').select('id').eq('tournament_id', tournamentId).is('poule_id', null)
        ])

        // Fetch tenant fields if available
        let tenantFields = []
        if (tournament?.tenant_id) {
            const { data: tenant } = await supabase.from('tenants').select('fields').eq('id', tournament.tenant_id).single()
            if (tenant?.fields) tenantFields = tenant.fields
        }

        const isKnockout = tournament?.type === 'knockout'

        // 2. Define Steps
        const steps = [
            // Issue #2: Knockout tournaments don't need poules or divisions - skip this step
            ...(!isKnockout ? [{
                id: 'divisions',
                label: 'Poules',
                // Issue #1: Only require poulesCount > 0 (divisions are auto-created)
                isComplete: poulesCount > 0,
                required: true,
                action: 'Maak Poules',
                link: `/admin/tournaments/${tournamentId}/divisions`
            }] : []),
            {
                id: 'fields',
                label: 'Velden',
                isComplete: fieldsCount > 0 || (tenantFields && tenantFields.length > 0),
                required: true,
                action: 'Beheer Velden',
                link: `/admin/settings`
            },
            {
                id: 'teams',
                label: 'Teams',
                isComplete: teamsCount >= 2,
                required: true,
                action: 'Teams Toevoegen',
                link: `/admin/tournaments/${tournamentId}/teams`
            },
            // Issue #2: Knockout tournaments don't need poule assignment - skip this step
            ...(!isKnockout ? [{
                id: 'assignments',
                label: 'Poule Indeling',
                isComplete: teamsCount > 0 && (teamsWithoutPoule?.length === 0),
                required: true,
                action: 'Deel Teams In',
                // Issue #3: Link to DivisionsPage (Indeling tab), not TeamsPage
                link: `/admin/tournaments/${tournamentId}/divisions`
            }] : [])
        ]

        // 3. Determine Blocker (First incomplete required step)
        const firstIncomplete = steps.find(s => s.required && !s.isComplete)

        return {
            isReady: !firstIncomplete,
            steps,
            blocker: firstIncomplete || null
        }

    } catch (err) {
        console.error('Validation error:', err)
        return { isReady: false, steps: [], blocker: { label: 'Error checking status' } }
    }
}
